import React, { useState, useEffect, useCallback } from 'react';
import { Pie, Line} from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, Title, BarElement } from 'chart.js';
import '../styles/summariesPanel.css';
import LoaderOverlay from '../components/LoaderOverlay';
import { showAppAlert } from '../utils/alertService';

const API_URL = process.env.REACT_APP_API_BASE_URL;

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, Title, BarElement);

function GraphicsDashboard() {
  const [clientsData, setClientsData] = useState([]);
  const [summaries, setSummaries] = useState(null);
  const [todaySummaries, setTodaySummaries] = useState(null);
  const [monthSummaries, setMonthSummaries] = useState(null);
  const [dailyTicketData, setDailyTicketData] = useState(null);
  const [monthlyTicketData, setMonthlyTicketData] = useState(null);
  
  // Inicializa as datas com o primeiro e último dia do mês atual
  const getInitialDates = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];
    return { firstDay, lastDay };
  };
  const [startDate, setStartDate] = useState(getInitialDates().firstDay);
  const [endDate, setEndDate] = useState(getInitialDates().lastDay);
  const [showGeneralSummaries, setShowGeneralSummaries] = useState(true);
  const [showTodaySummaries, setShowTodaySummaries] = useState(true);
  const [showMonthSummaries, setShowMonthSummaries] = useState(true);
  const [showTicketSection, setShowTicketSection] = useState(true);
  const [showDemographicsCharts, setShowDemographicsCharts] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const token = localStorage.getItem('token');

  // Função para buscar dados dos clientes da API
useEffect(() => {
    const fetchClientsData = async () => {
      try {
        const companyId = localStorage.getItem('company_id');
        const url = `${API_URL}/clients/custom-query?company_id=${companyId}`;
        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (response.ok) {
          const data = await response.json();
          setClientsData(Array.isArray(data) ? data : []);
        } else {
          setClientsData([]);
        }
      } catch (error) {
        console.error('Erro ao buscar dados dos clientes:', error);
        setClientsData([]);
      }
    };

    fetchClientsData();
  }, [token]);

  // Buscar os resumos
  useEffect(() => {
    const fetchSummaries = async () => {
      try {
        const companyId = localStorage.getItem('company_id');
        const response = await fetch(`${API_URL}/summaries?company_id=${companyId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        const data = await response.json();
        setSummaries(data);
      } catch (error) {
        console.error('Erro ao buscar resumos:', error);
      }
    };
    fetchSummaries();
  }, [token]);

  // Função para buscar os resumos de hoje
  useEffect(() => {
    const fetchTodaySummaries = async () => {
      try {
        const companyId = localStorage.getItem('company_id');
        const response = await fetch(`${API_URL}/operations/today-summaries?company_id=${companyId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        const data = await response.json();
        setTodaySummaries(data);
      } catch (error) {
        console.error('Erro ao buscar resumos de hoje:', error);
      }
    };

    fetchTodaySummaries();
  }, [token]);

  // Função para buscar os resumos do mês
  useEffect(() => {
    const fetchMonthSummaries = async () => {
      try {
        const companyId = localStorage.getItem('company_id');
        const response = await fetch(`${API_URL}/operations/month-summaries?company_id=${companyId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
        const data = await response.json();
        setMonthSummaries(data);
      } catch (error) {
        console.error('Erro ao buscar resumos do mês:', error);
      }
    };

    fetchMonthSummaries();
  }, [token]);

  // Função para buscar dados de ticket médio
  const fetchTicketData = useCallback(async (start, end) => {
    const searchStartDate = start || startDate;
    const searchEndDate = end || endDate;

    if (!searchStartDate || !searchEndDate) {
      showAppAlert('info', 'Por favor, selecione um período para buscar os dados.');
      return;
    }
    if (new Date(searchEndDate) < new Date(searchStartDate)) {
      showAppAlert('warning', 'A data de fim não pode ser anterior à data de início.');
      return;
    }

    setIsLoading(true);
    const companyId = localStorage.getItem('company_id');
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
    const body = JSON.stringify({
      company_id: companyId,
      start_date: searchStartDate,
      end_date: searchEndDate,
    });

    try {
      // Fetch Daily Ticket
      const dailyResponse = await fetch(`${API_URL}/operations/charts/daily-average-ticket`, {
        method: 'POST',
        headers,
        body,
      });
      if (dailyResponse.ok) {
        const data = await dailyResponse.json();
        setDailyTicketData(data);
      } else {
        setDailyTicketData(null);
        console.error('Erro ao buscar ticket médio diário');
      }

      // Fetch Monthly Ticket
      const monthlyResponse = await fetch(`${API_URL}/operations/charts/monthly-average-ticket`, {
        method: 'POST',
        headers,
        body,
      });
      if (monthlyResponse.ok) {
        const data = await monthlyResponse.json();
        setMonthlyTicketData(data);
      } else {
        setMonthlyTicketData(null);
        console.error('Erro ao buscar ticket médio mensal');
      }

    } catch (error) {
      console.error('Erro ao buscar dados de ticket médio:', error);
      showAppAlert('error', 'Ocorreu um erro de conexão ao buscar os dados de ticket.');
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate, token]);

  useEffect(() => {
    fetchTicketData(startDate, endDate);
  }, [fetchTicketData, startDate, endDate]);

  const getAge = (birthDateStr) => {
    if (!birthDateStr) return 0;
    const birthDate = new Date(birthDateStr);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Gráfico por Cidade
  const citiesCount = {};
  clientsData.forEach(client => {
    const city = client.cidade || 'Não informado';
    citiesCount[city] = (citiesCount[city] || 0) + 1;
  });
  const cityLabels = Object.keys(citiesCount);
  const cityData = Object.values(citiesCount);

  // Gráfico por Gênero
  const femaleCount = clientsData.filter(client => {
    const genero = client.genero;
    return genero && genero.toLowerCase() === 'feminino';
  }).length;
  
  const maleCount = clientsData.filter(client => {
    const genero = client.genero;
    return genero && genero.toLowerCase() === 'masculino';
  }).length;
  
  const unknownGenderCount = clientsData.filter(client => {
    const genero = client.genero;
    return !genero || (genero.toLowerCase() !== 'feminino' && genero.toLowerCase() !== 'masculino');
  }).length;

  const genderLabels = ['Feminino', 'Masculino'];
  const genderData = [femaleCount, maleCount];
  const genderColors = ['rgba(255, 99, 132, 0.6)', 'rgba(54, 162, 235, 0.6)'];

  // Se houver gêneros não informados, adicionar à lista
  if (unknownGenderCount > 0) {
    genderLabels.push('Não informado');
    genderData.push(unknownGenderCount);
    genderColors.push('rgba(128, 128, 128, 0.6)');
  }

  // Gráfico por Idade: agrupa em faixas etárias: -18, 19-35, 36-59 e 60+
  const ageRanges = {
    '-18': 0,
    '19-35': 0,
    '36-59': 0,
    '60+': 0,
  };
  clientsData.forEach(client => {
    const age = getAge(client.dataNascimento);
    if (age <= 18) {
      ageRanges['-18']++;
    } else if (age <= 35) {
      ageRanges['19-35']++;
    } else if (age <= 59) {
      ageRanges['36-59']++;
    } else {
      ageRanges['60+']++;
    }
  });
  const ageLabels = Object.keys(ageRanges);
  const ageData = Object.values(ageRanges);

  // Cores para os gráficos de Cidade e Idade
  const cityColors = ['rgba(255, 99, 132, 0.6)', 'rgba(54, 162, 235, 0.6)',
                       'rgba(255, 206, 86, 0.6)', 'rgba(75, 192, 192, 0.6)',
                       'rgba(153, 102, 255, 0.6)', 'rgba(255, 159, 64, 0.6)'];
  const ageColors = ['rgba(255, 206, 86, 0.6)', 'rgba(75, 192, 192, 0.6)',
                     'rgba(153, 102, 255, 0.6)', 'rgba(255, 159, 64, 0.6)'];

  const cityChartData = {
    labels: cityLabels,
    datasets: [{
      data: cityData,
      backgroundColor: cityColors.slice(0, cityLabels.length),
      borderColor: cityColors.slice(0, cityLabels.length).map(color => color.replace('0.6', '1')),
      borderWidth: 1,
    }],
  };

  const genderChartData = {
    labels: genderLabels,
    datasets: [{
      data: genderData,
      backgroundColor: genderColors.slice(0, genderLabels.length),
      borderColor: genderColors.slice(0, genderLabels.length).map(color => color.replace('0.6', '1')),
      borderWidth: 1,
    }],
  };

  const ageChartData = {
    labels: ageLabels,
    datasets: [{
      data: ageData,
      backgroundColor: ageColors,
      borderColor: ageColors.map(color => color.replace('0.6', '1')),
      borderWidth: 1,
    }],
  };

  // Gráfico de Ticket Médio Diário
  const dailyTicketChartData = {
    labels: dailyTicketData?.map(d => new Date(d.date).toLocaleDateString('pt-BR')) || [],
    datasets: [{
      label: 'Ticket Médio (R$)',
      data: dailyTicketData?.map(d => d.average_ticket) || [],
      fill: false,
      borderColor: 'rgb(75, 192, 192)',
      tension: 0.1,
    }],
  };

  // Gráfico de Ticket Médio Mensal
  const monthlyTicketChartData = {
    labels: monthlyTicketData?.map(d => {
      const [year, month] = d.month.split('-');
      return `${month}/${year}`;
    }) || [],
    datasets: [{
      label: 'Ticket Médio (R$)',
      data: monthlyTicketData?.map(d => d.average_ticket) || [],
      fill: false,
      borderColor: 'rgb(153, 102, 255)',
      tension: 0.1,
    }],
  };

  const lineChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Evolução do Ticket Médio',
      },
    },
    maintainAspectRatio: false,
  };

  const chartOptions = {
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
      },
    },
  };

  const genderChartOptions = {
    ...chartOptions,
    rotation: Math.PI,
  };

  return (
    <div className="dashboard-container">
      <LoaderOverlay isLoading={isLoading} message="Buscando dados dos gráficos..." />
      <fieldset className="dashboard-fieldset">
        <legend className="dashboard-legend" onClick={() => setShowGeneralSummaries(!showGeneralSummaries)}>
          Resumos Gerais {showGeneralSummaries ? '▲' : '▼'}
        </legend>
        {showGeneralSummaries && (
          <div className="cards-container">
            <div className="card card-small bg-primary">
              <h4 className="graphic-card">Total de Clientes</h4>
              <p className="graphic-card">{summaries ? summaries.total_clientes : '-'}</p>
            </div>
            <div className="card card-small bg-secondary">
              <h4 className="graphic-card">Total de Visitas</h4>
              <p className="graphic-card">{summaries ? summaries.total_visitas : '-'}</p>
            </div>
            <div className="card card-small bg-success">
              <h4 className="graphic-card">Pontos Ganhos</h4>
              <p className="graphic-card">{summaries ? summaries.saldo_pontos : '-'}</p>
            </div>
            <div className="card card-small bg-danger">
              <h4 className="graphic-card">Pontos Resgatados</h4>
              <p className="graphic-card">{summaries ? summaries.pontos_resgatados : '-'}</p>
            </div>
            <div className="card card-small bg-warning">
              <h4 className="graphic-card">Total Gasto</h4>
              <p className="graphic-card">
                {summaries ? `R$ ${parseFloat(summaries.total_gasto).toFixed(2)}` : '-'}
              </p>
            </div>
          </div>
        )}
      </fieldset>

      <fieldset className="dashboard-fieldset">
        <legend className="dashboard-legend" onClick={() => setShowTodaySummaries(!showTodaySummaries)}>
          Resumos de Hoje {showTodaySummaries ? '▲' : '▼'}
        </legend>
        {showTodaySummaries && (
          <div className="cards-container">
            <div className="card card-small bg-secondary" style={{ backgroundColor: '#6c757d' }}>
              <h4 className="graphic-card">Total de Visitas Hoje</h4>
              <p className="graphic-card">{todaySummaries ? todaySummaries.total_visitas : '-'}</p>
            </div>
            <div className="card card-small bg-success">
              <h4 className="graphic-card">Pontos Ganhos Hoje</h4>
              <p className="graphic-card">{todaySummaries ? todaySummaries.saldo_pontos : '-'}</p>
            </div>
            <div className="card card-small bg-danger">
              <h4 className="graphic-card">Pontos Resgatados Hoje</h4>
              <p className="graphic-card">{todaySummaries ? todaySummaries.pontos_resgatados : '-'}</p>
            </div>
            <div className="card card-small bg-warning">
              <h4 className="graphic-card">Total Gasto Hoje</h4>
              <p className="graphic-card">
                {todaySummaries ? `R$ ${parseFloat(todaySummaries.total_gasto).toFixed(2)}` : '-'}
              </p>
            </div>
          </div>
        )}
      </fieldset>

      <fieldset className="dashboard-fieldset">
        <legend className="dashboard-legend" onClick={() => setShowMonthSummaries(!showMonthSummaries)}>
          Resumos dos Últimos 30 Dias {showMonthSummaries ? '▲' : '▼'}
        </legend>
        {showMonthSummaries && (
          <div className="cards-container">
            <div className="card card-small bg-secondary" style={{ backgroundColor: '#6c757d' }}>
              <h4 className="graphic-card">Total de Visitas (30 dias)</h4>
              <p className="graphic-card">{monthSummaries ? monthSummaries.total_visitas : '-'}</p>
            </div>
            <div className="card card-small bg-success">
              <h4 className="graphic-card">Pontos Ganhos (30 dias)</h4>
              <p className="graphic-card">{monthSummaries ? monthSummaries.saldo_pontos : '-'}</p>
            </div>
            <div className="card card-small bg-danger">
              <h4 className="graphic-card">Pontos Resgatados (30 dias)</h4>
              <p className="graphic-card">{monthSummaries ? monthSummaries.pontos_resgatados : '-'}</p>
            </div>
            <div className="card card-small bg-warning">
              <h4 className="graphic-card">Total Gasto (30 dias)</h4>
              <p className="graphic-card">
                {monthSummaries ? `R$ ${parseFloat(monthSummaries.total_gasto).toFixed(2)}` : '-'}
              </p>
            </div>
          </div>
        )}
      </fieldset>

      <fieldset className="dashboard-fieldset">
        <legend className="dashboard-legend" onClick={() => setShowTicketSection(!showTicketSection)}>
          Ticket Médio {showTicketSection ? '▲' : '▼'}
        </legend>
        {showTicketSection && (
          <>
            {/* Filtros para Gráficos de Ticket */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '20px', marginBottom: '20px', padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
              <div className="filter-group">
                <label htmlFor="startDate">Data de Início:</label><br />
                <input
                  type="date"
                  id="startDate"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="input-style"
                />
              </div>
              <div className="filter-group">
                <label htmlFor="endDate">Data de Fim:</label><br />
                <input
                  type="date"
                  id="endDate"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="input-style"
                  disabled={!startDate}
                  min={startDate}
                />
              </div>
              <div style={{ alignSelf: 'flex-end' }}>
                <button onClick={() => fetchTicketData()} className="button-style" disabled={isLoading || !startDate || !endDate}>
                  Buscar
                </button>
              </div>
            </div>

            {/* Gráficos de Ticket Médio */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', justifyContent: 'center', marginBottom: '40px' }}>
              <div className="chart-container" style={{ height: '300px' }}>
                <h3>Ticket Médio por Dia</h3>
                {dailyTicketData ? <Line options={lineChartOptions} data={dailyTicketChartData} /> : <p>Selecione um período para ver os dados.</p>}
              </div>
              <div className="chart-container" style={{ height: '300px' }}>
                <h3>Ticket Médio por Mês</h3>
                {monthlyTicketData ? <Line options={lineChartOptions} data={monthlyTicketChartData} /> : <p>Selecione um período para ver os dados.</p>}
              </div>
            </div>
          </>
        )}
      </fieldset>

      <fieldset className="dashboard-fieldset">
        <legend className="dashboard-legend" onClick={() => setShowDemographicsCharts(!showDemographicsCharts)}>
          Dados Demográficos dos Clientes {showDemographicsCharts ? '▲' : '▼'}
        </legend>
        {showDemographicsCharts && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', justifyContent: 'center' }}>
            <div className="chart-container-pie">
              <h3>Clientes por Cidade</h3>
              <Pie data={cityChartData} options={chartOptions} />
            </div>
            <div className="chart-container-pie">
              <h3>Clientes por Gênero</h3>
              <Pie data={genderChartData} options={genderChartOptions} />
            </div>
            <div className="chart-container-pie">
              <h3>Clientes por Faixa Etária</h3>
              <Pie data={ageChartData} options={chartOptions} />
            </div>
          </div>
        )}
      </fieldset>
    </div>
  );
}
export default GraphicsDashboard;
